import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";

export function ProPicker({ selected, onChange, testIdPrefix = "skip-visit" }) {
  const [pros, setPros] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => {
    api.get("/users?role=professional&status=approved&masters_only=true")
      .then((r) => setPros((r.data || []).map((p) => ({ ...p, _id: p._id || p.id }))))
      .catch(() => setPros([]));
  }, []);
  const name = (p) => (p.company_name || `${p.first_name || ""} ${p.last_name || ""}`.trim() || p.email);
  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? pros.filter((p) => `${name(p)} ${p.email}`.toLowerCase().includes(s)) : pros;
  }, [pros, q]);
  const toggle = (id) => onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  return (
    <div className="space-y-2" data-testid={`${testIdPrefix}-pros-picker`}>
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca professionista…" data-testid={`${testIdPrefix}-pros-search`} />
      <div className="max-h-48 overflow-y-auto rounded-md border divide-y bg-white">
        {shown.length === 0 && <div className="p-3 text-xs text-muted-foreground">Nessun professionista trovato</div>}
        {shown.map((p) => (
          <label key={p._id} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50">
            <input type="checkbox" checked={selected.includes(p._id)} onChange={() => toggle(p._id)} data-testid={`${testIdPrefix}-pro-${p._id}`} />
            <span className="flex-1 min-w-0 truncate">{name(p)}</span>
            <span className="text-[11px] text-muted-foreground truncate">{p.email}</span>
          </label>
        ))}
      </div>
      <div className="text-[11px] text-muted-foreground" data-testid={`${testIdPrefix}-pros-count`}>{selected.length} selezionati</div>
    </div>
  );
}

export function SkipVisitPricingFields({ form, setForm }) {
  const set = (patch) => setForm({ ...form, ...patch });
  const scope = form.skip_visit_pricing_scope || "all";
  return (
    <div className="pl-3 space-y-3" data-testid="skip-visit-pricing-section">
      <label className="flex items-center gap-2 text-sm font-semibold text-slate-800 cursor-pointer">
        <input
          type="checkbox"
          checked={!!form.skip_visit_pricing_enabled}
          onChange={(e) => set({ skip_visit_pricing_enabled: e.target.checked })}
          data-testid="skip-visit-pricing-toggle"
        />
        Imposta prezzo/sconto per scelta sopralluogo
      </label>
      {form.skip_visit_pricing_enabled && (
        <div className="space-y-3 p-3 rounded-md border bg-amber-50/30">
          <p className="text-[11px] text-slate-600">Variazione in € sul prezzo del servizio (usa il segno meno per uno sconto). Viene applicata prima dello sconto fascia.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-700">Se sceglie "Salta sopralluogo" (€)</label>
              <Input type="number" step="0.01" value={form.skip_visit_price_skip ?? 0}
                onChange={(e) => set({ skip_visit_price_skip: e.target.value })} className="mt-1" data-testid="skip-visit-price-skip-input" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-700">Se sceglie "Non saltare" (€)</label>
              <Input type="number" step="0.01" value={form.skip_visit_price_no_skip ?? 0}
                onChange={(e) => set({ skip_visit_price_no_skip: e.target.value })} className="mt-1" data-testid="skip-visit-price-no-skip-input" />
            </div>
          </div>
          <div className="flex flex-wrap gap-4 text-sm">
            {[["all", "Tutti i professionisti"], ["selected", "Seleziona professionisti"]].map(([v, l]) => (
              <label key={v} className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="skip-visit-scope" checked={scope === v} onChange={() => set({ skip_visit_pricing_scope: v })} data-testid={`skip-visit-scope-${v}`} />
                {l}
              </label>
            ))}
          </div>
          {scope === "selected" && (
            <ProPicker
              selected={form.skip_visit_pricing_professionals || []}
              onChange={(ids) => set({ skip_visit_pricing_professionals: ids })}
            />
          )}
        </div>
      )}
    </div>
  );
}
